import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import ReactDOMServer from "react-dom/server";

function buildPinIcon(color) {
  const html = `
    <div style="
      width:30px;height:42px;position:relative;
      filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    ">
      <div style="
        width:30px;height:30px;border-radius:50% 50% 50% 0;
        background:${color};transform:rotate(-45deg);
        border:3px solid #fff;position:absolute;left:0;top:0;
      "></div>
      <div style="
        position:absolute;left:9px;top:9px;width:12px;height:12px;
        background:#fff;border-radius:50%;
      "></div>
    </div>`;
  return L.divIcon({
    html,
    className: "client-map-pin",
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -38],
  });
}

/**
 * StrictMode-safe multi-marker map using Leaflet directly (no react-leaflet).
 * Avoids the known "Map container is already initialized" bug.
 */
export function MultiMarkerMap({
  markers = [],
  center = [39.8283, -98.5795],
  zoom = 4,
  zoomSingle = 12,
  height = "60vh",
}) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [pinHover, setPinHover] = useState(null);

  // Init map once
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      center,
      zoom,
      scrollWheelZoom: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);
    mapRef.current = map;
    markersLayerRef.current = L.layerGroup().addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update markers
  useEffect(() => {
    const layer = markersLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    const valid = markers.filter((m) => m.lat && m.lng);
    valid.forEach((m) => {
      const mk = L.marker([m.lat, m.lng], { icon: buildPinIcon(m.color || "#2563EB") });
      if (m.popupNode) {
        mk.bindPopup(m.popupNode);
      } else if (m.popupHTML) {
        mk.bindPopup(m.popupHTML);
      }
      mk.addTo(layer);
    });
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], zoomSingle);
    } else if (valid.length > 1) {
      const bounds = L.latLngBounds(valid.map((m) => [m.lat, m.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [markers, zoomSingle]);

  return (
    <div
      ref={elRef}
      style={{ height, width: "100%" }}
      data-testid="multi-marker-map"
    />
  );
}

export function renderPopupToHTML(node) {
  try {
    return ReactDOMServer.renderToStaticMarkup(node);
  } catch {
    return "";
  }
}
